import { Suspense } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireRole } from '@/components/RequireRole';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AppLayout } from '@/components/layout/AppLayout';
import { LogRocketProvider } from '@/components/LogRocketProvider';
import { ROUTES } from '@/constants/routes';
import { defaultRouteFor, useCurrentUser } from '@/hooks/useCurrentUser';
import Picasso, { Favicon } from '@toptal/picasso-provider';
import { Loader } from '@toptal/picasso';

const Login = lazyWithRetry(() => import('@/routes/auth/Login'));
const Signup = lazyWithRetry(() => import('@/routes/auth/Signup'));
const Welcome = lazyWithRetry(() => import('@/routes/auth/Welcome'));
const InviteExpired = lazyWithRetry(() => import('@/routes/auth/InviteExpired'));
const ForgotPassword = lazyWithRetry(() => import('@/routes/auth/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('@/routes/auth/ResetPassword'));
const ResetPasswordExpired = lazyWithRetry(() => import('@/routes/auth/ResetPasswordExpired'));
const VerifyEmail = lazyWithRetry(() => import('@/routes/auth/VerifyEmail'));
const ConsultantAssessments = lazyWithRetry(() => import('@/routes/consultant/Assessments'));
const AssessmentTake = lazyWithRetry(() => import('@/routes/consultant/AssessmentTake'));
const AssessmentReport = lazyWithRetry(() => import('@/routes/consultant/AssessmentReport'));
const Help = lazyWithRetry(() => import('@/routes/consultant/Help'));
const AdminAssessments = lazyWithRetry(() => import('@/routes/admin/Assessments'));
const AdminAssessmentAddCompany = lazyWithRetry(
  () => import('@/routes/admin/AssessmentAddCompany'),
);
const AdminFrameworks = lazyWithRetry(() => import('@/routes/admin/Frameworks'));
const AdminFrameworkDetail = lazyWithRetry(() => import('@/routes/admin/FrameworkDetail'));
const AdminDomains = lazyWithRetry(() => import('@/routes/admin/Domains'));
const AdminDomainMappings = lazyWithRetry(() => import('@/routes/admin/DomainMappings'));
const AdminIndustryBenchmarks = lazyWithRetry(() => import('@/routes/admin/IndustryBenchmarks'));
const AdminUsers = lazyWithRetry(() => import('@/routes/admin/Users'));
const AdminAuditLog = lazyWithRetry(() => import('@/routes/admin/AuditLog'));
const AdminTalentBench = lazyWithRetry(() => import('@/routes/admin/TalentBench'));

const AuthLayoutRoute = () => (
  <AuthLayout>
    <Outlet />
  </AuthLayout>
);

const ProtectedLayout = () => (
  <RequireAuth>
    <AppLayout />
  </RequireAuth>
);

const ProtectedFullScreen = () => (
  <RequireAuth>
    <Outlet />
  </RequireAuth>
);

const RoleRootRedirect = () => {
  const { role } = useCurrentUser();
  return <Navigate to={defaultRouteFor(role)} replace />;
};

const router = createBrowserRouter([
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AuthLayoutRoute />,
        children: [
          { path: ROUTES.login(), element: <Login /> },
          { path: ROUTES.signup(), element: <Signup /> },
          { path: ROUTES.welcome(), element: <Welcome /> },
          { path: ROUTES.forgotPassword(), element: <ForgotPassword /> },
          { path: ROUTES.resetPassword(), element: <ResetPassword /> },
          { path: ROUTES.verifyEmail(), element: <VerifyEmail /> },
        ],
      },
      { path: ROUTES.resetPasswordExpired(), element: <ResetPasswordExpired /> },
      { path: ROUTES.inviteExpired(), element: <InviteExpired /> },
      {
        element: (
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        ),
        children: [
          { path: '/assessments/:id/take', element: <AssessmentTake /> },
          { path: '/assessments/:id/report', element: <AssessmentReport /> },
        ],
      },
      {
        element: <ProtectedLayout />,
        children: [
          { path: ROUTES.root(), element: <RoleRootRedirect /> },
          {
            // Clients share the assessments list view, but the backend scopes
            // it to their company's completed assessments (read-only), so they
            // only ever see report links — no in-progress/take actions.
            element: <RequireRole role={['consultant', 'client']} />,
            children: [{ path: ROUTES.assessments(), element: <ConsultantAssessments /> }],
          },
          {
            element: <RequireRole role="consultant" />,
            children: [{ path: ROUTES.help(), element: <Help /> }],
          },
          {
            element: <RequireRole role="admin" />,
            children: [
              { path: ROUTES.admin(), element: <AdminAssessments /> },
              { path: ROUTES.adminFrameworks(), element: <AdminFrameworks /> },
              { path: '/admin/frameworks/:id', element: <AdminFrameworkDetail /> },
              { path: ROUTES.adminDomains(), element: <AdminDomains /> },
              { path: '/admin/domains/:id/mappings', element: <AdminDomainMappings /> },
              { path: ROUTES.adminIndustryBenchmarks(), element: <AdminIndustryBenchmarks /> },
              { path: ROUTES.adminUsers(), element: <AdminUsers /> },
              { path: ROUTES.adminAuditLog(), element: <AdminAuditLog /> },
              { path: ROUTES.adminTalentBench(), element: <AdminTalentBench /> },
            ],
          },
        ],
      },
      {
        element: <ProtectedFullScreen />,
        children: [
          {
            element: <RequireRole role="admin" />,
            children: [
              { path: ROUTES.adminAssessmentsCreate(), element: <AdminAssessmentAddCompany /> },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to={ROUTES.root()} replace /> },
    ],
  },
]);

export function App() {
  return (
    <Picasso injectFirst preventPageWidthChangeOnScrollbar={false}>
      <Favicon environment="production" />
      <QueryClientProvider client={queryClient}>
        <LogRocketProvider>
          <Suspense
            fallback={
              <Loader size="small" className="mt-2">
                Loading...
              </Loader>
            }
          >
            <RouterProvider router={router} />
          </Suspense>
        </LogRocketProvider>
      </QueryClientProvider>
    </Picasso>
  );
}
